import { configureStore, createAction, createReducer, type EnhancedStore } from '@reduxjs/toolkit';
import type { ToastProps, ToastPropsBase } from '../../hooks/Toast/useToasts';

export type GlobalToastStoreType = {
  toasts: {
    uiToasts: ToastProps[];
    appInboxToasts: ToastPropsBase[];
  };
};
type GlobalToastsReduxState = {
  uiToasts: ToastProps[];
  appInboxToasts: ToastPropsBase[];
};

const initialState: GlobalToastsReduxState = {
  uiToasts: [],
  appInboxToasts: [],
};

export type AddActionPayload<T extends ToastPropsBase = ToastPropsBase> =
  | { type: 'ui'; value: T & { id: string } }
  | { type: 'appInbox'; value: T };
// Redux Actions
const add = createAction<AddActionPayload>('toasts/add');
const remove = createAction<{ type: 'ui' | 'appInbox'; id: string }>('toasts/remove');
const clear = createAction<{ type: 'ui' | 'appInbox' }>('toasts/clear');

const globalToastsReducer = createReducer(initialState, builder => {
  builder.addCase(add, (state, action) => {
    if (action.payload.type === 'ui') {
      const toastValue = action.payload.value;
      state.uiToasts.push(toastValue as ToastProps);
    } else {
      const toastValue = action.payload.value;
      state.appInboxToasts.push(toastValue);
    }
  });
  builder.addCase(remove, (state, action) => {
    if (action.payload.type === 'ui') {
      state.uiToasts = state.uiToasts.filter(toast => toast.id !== action.payload.id);
    } else {
      state.appInboxToasts = state.appInboxToasts.filter(toast => toast.id !== action.payload.id);
    }
  });
  builder.addCase(clear, (state, action) => {
    if (action.payload.type === 'ui') {
      state.uiToasts = [];
    } else {
      state.appInboxToasts = [];
    }
  });
});

// Function to Add Toast with Timeout
export const addToastWithTimeout =
  <T extends ToastPropsBase>(payload: AddActionPayload<T>, onRemove?: (id: string) => void) =>
  (dispatch: GlobalToastDispatch) => {
    dispatch(add(payload));

    if (payload.value.timeout != null) {
      setTimeout(() => {
        if (payload.value.id) {
          dispatch(remove({ type: payload.type, id: payload.value.id }));
          onRemove?.(payload.value.id);
        }
      }, payload.value.timeout);
    }
  };

// Store solely for Toasts -
export const globalToastStore = configureStore<GlobalToastStoreType>({
  reducer: {
    toasts: globalToastsReducer,
  },
});

export type ReduxSelectorTypes<TStore extends EnhancedStore, TDispatch> = {
  storeType: TStore;
  dispatchType: TDispatch;
  selectorType: ReturnType<TStore['getState']>;
};

export type GlobalToastDispatch = typeof globalToastStore.dispatch;

export const globalToastsDispatch = globalToastStore.dispatch;
export const getGlobalToastsState = globalToastStore.getState;

// Redux Reducer
export const globalToastActions = {
  addToastWithTimeout,
  remove,
  clear,
};
